import { useT } from "@transifex/react";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import { ButtonColorVariant, ButtonVariant, AreaButton } from "../AreaButton";
import { ListItemWithCheck } from "../ListItemWithCheck";
import { CardVariant, ImageSize, type CardCommonProps } from "./Card.type";
import { CardHeader } from "./CardHeader";

export const VerticalCard = (props: CardCommonProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t = useT();
    const t_sr_card = t("Card:", { _tags: "TeacherArea, ScreenReaderOnly" });

    const smallImgSize = "h-[150px]";
    const bigImgSize = getStyleBasedOnBreakpoint(Breakpoint.MD, "h-[250px]", "h-[150px]");
    const imgSize = props.image.size === ImageSize.BIG ? bigImgSize : smallImgSize;

    const cardClasses = "rounded-medium border-stone-400 flex flex-col overflow-hidden border bg-white w-full";

    const isClickable = props?.href;

    const hoverClasses = isClickable
        ? "group hover:outline hover:outline-stone-400 hover:bg-background-light cursor-pointer"
        : "cursor-not-allowed";
    const imageHoverClasses = isClickable ? "group-hover:brightness-95" : "opacity-50";
    const descriptionColor = isClickable ? "text-stone-1000" : "text-stone-500";

    const Tag = props.href ? "a" : "div";
    const tagProps: { [key: string]: any } = {
        "aria-label": props.ariaLabel ?? `${t_sr_card} ${props.title}`,
        className: `${cardClasses} ${hoverClasses}`,
    };

    if (props["data-testid"]) {
        tagProps["data-testid"] = props["data-testid"];
    }

    if (Tag === "a") {
        tagProps.href = props.href;
        tagProps.role = "link";
    } else {
        tagProps.tabIndex = 0;
    }

    return (
        <Tag {...tagProps}>
            <div className={`w-full ${props.colorForTransparentImage} ${imgSize}`}>
                <img
                    src={props.image.src}
                    alt={props.image.alt}
                    className={`h-full w-full ${
                        props.colorForTransparentImage ? "object-contain" : "object-cover"
                    } ${imageHoverClasses}`}
                />
            </div>

            <div className="p-m gap-m flex flex-col">
                <div className="gap-s flex flex-col">
                    <CardHeader
                        variant={CardVariant.VERTICAL}
                        title={props.title}
                        subtitle={props.subtitle}
                        level={props.headlineLevel ?? 3}
                        isDisabled={!isClickable}
                    />
                    {props.description && (
                        <p className={`text-m text-center ${descriptionColor}`}>{props.description}</p>
                    )}
                </div>

                {props.checkmarks && (
                    <ul className="gap-xs flex flex-col">
                        {props.checkmarks.map((text) => (
                            <ListItemWithCheck
                                key={`verticalCard-list-item${text.replace(/\s/g, "")}`}
                                text={text}
                            />
                        ))}
                    </ul>
                )}

                {props.primaryButton?.text && (
                    <AreaButton
                        variant={ButtonVariant.PRIMARY}
                        colorVariant={ButtonColorVariant.LUMINA}
                        buttonId={props.primaryButton.buttonId}
                        role={props.primaryButton.role}
                        iconName={props.primaryButton.iconName}
                    >
                        {props.primaryButton?.text}
                    </AreaButton>
                )}
            </div>
        </Tag>
    );
};
